/**
 * Textarea Component
 * Multi-line text input with label, validation, and character count
 */

import type { TextareaHTMLAttributes } from 'react';
import { useState } from 'react';

export interface TextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  label?: string;
  error?: string;
  helperText?: string;
  required?: boolean;
  fullWidth?: boolean;
  showCharCount?: boolean;
  resize?: 'none' | 'vertical' | 'horizontal' | 'both';
}

export function Textarea({
  label,
  error,
  helperText,
  required = false,
  fullWidth = false,
  showCharCount = false,
  resize = 'vertical',
  className = '',
  disabled = false,
  id,
  maxLength,
  value,
  onChange,
  ...props
}: TextareaProps) {
  const [charCount, setCharCount] = useState(
    value ? String(value).length : 0
  );

  const textareaId = id || `textarea-${Math.random().toString(36).substr(2, 9)}`;

  const resizeStyles = {
    none: 'resize-none',
    vertical: 'resize-y',
    horizontal: 'resize-x',
    both: 'resize',
  };

  const baseStyles = `
    block px-4 py-2 text-base rounded-md border shadow-sm
    focus:outline-none focus:ring-2 focus:ring-offset-2
    disabled:bg-gray-100 disabled:cursor-not-allowed disabled:text-gray-500
    transition-colors
  `;

  const stateStyles = error
    ? 'border-red-300 text-red-900 placeholder-red-300 focus:border-red-500 focus:ring-red-500'
    : 'border-gray-300 focus:border-blue-500 focus:ring-blue-500';

  const widthStyles = fullWidth ? 'w-full' : '';

  const textareaClasses = `
    ${baseStyles}
    ${stateStyles}
    ${widthStyles}
    ${resizeStyles[resize]}
    ${className}
  `.trim().replace(/\s+/g, ' ');

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    if (showCharCount) {
      setCharCount(e.target.value.length);
    }
    onChange?.(e);
  };

  return (
    <div className={fullWidth ? 'w-full' : ''}>
      {label && (
        <label
          htmlFor={textareaId}
          className="block text-sm font-medium text-gray-700 mb-1"
        >
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </label>
      )}

      <textarea
        id={textareaId}
        className={textareaClasses}
        disabled={disabled}
        maxLength={maxLength}
        value={value}
        onChange={handleChange}
        aria-invalid={error ? 'true' : 'false'}
        aria-describedby={
          error ? `${textareaId}-error` : helperText ? `${textareaId}-helper` : undefined
        }
        {...props}
      />

      <div className="flex items-center justify-between mt-1">
        <div className="flex-1">
          {error && (
            <p
              id={`${textareaId}-error`}
              className="text-sm text-red-600"
              role="alert"
            >
              {error}
            </p>
          )}

          {!error && helperText && (
            <p id={`${textareaId}-helper`} className="text-sm text-gray-500">
              {helperText}
            </p>
          )}
        </div>

        {showCharCount && maxLength && (
          <p
            className={`text-sm ml-4 ${
              charCount > maxLength ? 'text-red-600' : 'text-gray-500'
            }`}
          >
            {charCount}/{maxLength}
          </p>
        )}
      </div>
    </div>
  );
}
